import {
  getCallJournal,
  getFaxes,
  getRecordings,
  getVoicemails,
  getSmsConversations,
} from "./utils";

export const parseNewItems = (data = []) => data.filter((item) => item.viewed === "no");

export const parseUnreadMessages = (data = []) => data.filter((item) => item.unreadMessages !== 0);

export const isUnauthorized = (results) =>
  results.some((res) => res instanceof Error && res.message === "Unauthorized");

// The call journal is the most expensive of the five lists for the server,
// and every list view reloads all five when it opens or changes tab. So the
// journal is asked for again only when it matters: on the Call Journal and
// Notifications pages, after a call ends, or when the copy is older than this.
const JOURNAL_MAX_AGE_MS = 60000;
let journalCache = { key: "", at: 0, result: null };
// A journal request that has not been answered yet. At login the dashboard
// and the Call Journal both ask at the same moment; the second one waits for
// the first instead of making the server run the same query twice at once.
let journalPending = { key: "", promise: null };

// Forget the copy, so the next reload asks the server (a call just ended).
export const invalidateJournal = () => {
  journalCache = { key: "", at: 0, result: null };
};

// How many calls the server sends at a time.
export const JOURNAL_PAGE_SIZE = 100;

// Whether the server has calls older than the ones it last sent. An older
// server does not say; then a full page is taken to mean there may be more.
export const journalHasMore = () => {
  const result = journalCache.result;
  if (!result) return false;
  if (result.paging && typeof result.paging.hasMore === "boolean") return result.paging.hasMore;
  return Array.isArray(result.journalEntries) && result.journalEntries.length >= JOURNAL_PAGE_SIZE;
};

const loadJournal = (values, setCalls, fresh) => {
  const key = `${values.server}|${values.token}`;
  const usable = !fresh && journalCache.result && journalCache.key === key &&
    Date.now() - journalCache.at < JOURNAL_MAX_AGE_MS;
  if (usable) {
    return Promise.resolve(journalCache.result);
  }
  if (journalPending.promise && journalPending.key === key) {
    return journalPending.promise;
  }
  const promise = getCallJournal(values, setCalls).then((result) => {
    if (journalPending.promise === promise) journalPending = { key: "", promise: null };
    // Only a good answer is kept; an error is asked for again next time.
    if (result && !(result instanceof Error) && Array.isArray(result.journalEntries)) {
      journalCache = { key, at: Date.now(), result };
    }
    return result;
  });
  journalPending = { key, promise };
  return promise;
};

// The SMS conversation list gets the same treatment. Whoever needs the
// server's copy (the SMS page, a live SMS event, the Notifications page) asks
// through this function, so the remembered copy is always the newest one
// anybody received. `fresh` false reuses a copy less than a minute old.
let smsCache = { key: "", at: 0, result: null };

export const loadSmsConversations = (values, setSmsConversations, fresh = true) => {
  const key = `${values.server}|${values.token}`;
  const usable = !fresh && smsCache.result && smsCache.key === key &&
    Date.now() - smsCache.at < JOURNAL_MAX_AGE_MS;
  if (usable) {
    return Promise.resolve(smsCache.result);
  }
  return getSmsConversations(values, setSmsConversations).then((result) => {
    // A good answer is kept, and so is "Not Found", which is how the server
    // says there are no conversations. Any other error is asked for again.
    const none = result instanceof Error && result.message === "Not Found";
    if (none || (result && !(result instanceof Error) && Array.isArray(result.Conversations))) {
      smsCache = { key, at: Date.now(), result };
    }
    return result;
  });
};

/**
 * Reloads the five notification sources (journal, faxes, recordings,
 * voicemails, SMS conversations) plus any extra fetches, stores each result
 * through its setter and recomputes the notification badges.
 *
 * `extra` promises are appended after the five standard ones, so callers can
 * read them from the resolved array starting at index 5.
 *
 * `freshJournal` and `freshSms` ask the server for the call journal and
 * the SMS conversations even when a recent copy exists (see
 * JOURNAL_MAX_AGE_MS).
 */
export const refreshNotifications = ({
  values,
  setters,
  extra = [],
  setNotifications,
  setNotificationsLoading,
  onUnauthorized,
  freshJournal = false,
  freshSms = false,
}) => {
  const { setCalls, setFaxes, setRecordings, setVoicemails, setSmsConversations } = setters;

  setNotificationsLoading(true);

  return Promise.all([
    loadJournal(values, setCalls, freshJournal),
    getFaxes(values, setFaxes),
    getRecordings(values, setRecordings),
    getVoicemails(values, setVoicemails),
    loadSmsConversations(values, setSmsConversations, freshSms),
    ...extra,
  ])
    .then((res) => {
      if (isUnauthorized(res)) {
        onUnauthorized();
        return res;
      }

      setNotifications({
        call: parseNewItems(res[0].journalEntries),
        fax: parseNewItems(res[1].faxDocuments),
        recording: parseNewItems(res[2].callRecordings),
        voicemail: parseNewItems(res[3].voicemailRecordings),
        conversation: parseUnreadMessages(res[4].Conversations),
      });
      setNotificationsLoading(false);
      return res;
    })
    .catch((err) => {
      setNotificationsLoading(false);
      console.error(err);
    });
};
